/** Дрібні спільні компоненти інтерфейсу. */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { back } from '../lib/router.ts'
import { canRecord, play, playBlob, startRecording, stop, unlockAudio, type Recorder } from '../lib/audio.ts'
import { recordings } from '../lib/db.ts'
import type { Note, Phrase } from '../lib/course.ts'

export function TopBar({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div className="topbar">
      <button className="back" onClick={back} aria-label="Назад">←</button>
      <h2>{title}</h2>
      {right}
    </div>
  )
}

export function Bar({ value, max, good }: { value: number; max: number; good?: boolean }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
  return (
    <div className={`bar${good ? ' bar--good' : ''}`} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className="bar__fill" style={{ width: `${pct}%` }} />
    </div>
  )
}

export function Tile({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="tile">
      <div className="tile__value">{value}</div>
      <div className="tile__label">{label}</div>
    </div>
  )
}

/** Кнопка озвучення. Довге натискання програє повільно — для розбору звуків. */
export function Speak({
  audio,
  text,
  small,
  auto,
}: {
  audio?: string
  text?: string
  small?: boolean
  /** Програти одразу після появи. */
  auto?: boolean
}) {
  const [playing, setPlaying] = useState(false)
  const timer = useRef<number | null>(null)
  const slowed = useRef(false)

  const run = async (rate: number) => {
    unlockAudio()
    setPlaying(true)
    await play(audio, { text, rate })
    setPlaying(false)
  }

  useEffect(() => {
    if (auto) void run(1)
    return () => stop()
    // Автопрогравання прив'язане саме до конкретної репліки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio, text])

  const down = () => {
    slowed.current = false
    timer.current = window.setTimeout(() => {
      slowed.current = true
      void run(0.65)
    }, 450)
  }
  const up = () => {
    if (timer.current) clearTimeout(timer.current)
    if (!slowed.current) void run(1)
  }

  return (
    <button
      className={`speak${small ? ' speak--sm' : ''}${playing ? ' is-playing' : ''}`}
      onPointerDown={down}
      onPointerUp={up}
      onPointerLeave={() => timer.current && clearTimeout(timer.current)}
      aria-label="Прослухати"
      title="Натисни — звичайно, утримуй — повільно"
    >
      {playing ? '🔉' : '🔊'}
    </button>
  )
}

const NOTE_ICON: Record<Note['kind'], string> = {
  cognate: '🤝',
  'false-friend': '⚠️',
  pattern: '🧩',
  pronunciation: '👄',
  usage: '💬',
  culture: '📍',
}

const NOTE_TITLE: Record<Note['kind'], string> = {
  cognate: 'Схоже на українську',
  'false-friend': 'Хибний друг',
  pattern: 'Закономірність',
  pronunciation: 'Вимова',
  usage: 'Як вживається',
  culture: 'Місцева реалія',
}

export function NoteView({ note }: { note: Note }) {
  return (
    <div className={`note note--${note.kind}`}>
      <span className="note__icon" aria-hidden>{NOTE_ICON[note.kind]}</span>
      <span>
        <strong className="tiny" style={{ display: 'block', color: 'var(--text-faint)', fontWeight: 600 }}>
          {NOTE_TITLE[note.kind]}
        </strong>
        {note.uk}
      </span>
    </div>
  )
}

/** Мітки біля фрази: когнат, хибний друг тощо. */
export function PhraseChips({ phrase }: { phrase: Phrase }) {
  const notes = phrase.notes ?? []
  const ff = notes.some((n) => n.kind === 'false-friend')
  const cog = notes.some((n) => n.kind === 'cognate')
  if (!ff && !cog) return null
  return (
    <div className="row" style={{ gap: 6, justifyContent: 'center' }}>
      {ff && <span className="chip chip--danger">⚠️ хибний друг</span>}
      {cog && <span className="chip chip--good">🤝 когнат</span>}
    </div>
  )
}

/**
 * Мінімальна розмітка для пояснень: **жирний**, `код`, порожній рядок — абзац.
 * Повноцінний markdown тут не потрібен, а залежність коштувала б 40 КБ.
 */
export function Prose({ text }: { text: string }) {
  return (
    <div className="prose">
      {text.split('\n\n').map((para, i) => (
        <p key={i}>{inline(para)}</p>
      ))}
    </div>
  )
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\n)/g
  let last = 0
  let m: RegExpExecArray | null
  let key = 0
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    if (tok === '\n') out.push(<br key={key++} />)
    else if (tok.startsWith('**')) out.push(<strong key={key++}>{tok.slice(2, -2)}</strong>)
    else out.push(<code key={key++}>{tok.slice(1, -1)}</code>)
    last = m.index + tok.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

export function Empty({ emoji, title, hint }: { emoji: string; title: string; hint?: string }) {
  return (
    <div className="empty">
      <span className="empty__emoji" aria-hidden>{emoji}</span>
      <div style={{ fontWeight: 600, color: 'var(--text-dim)' }}>{title}</div>
      {hint && <div className="small" style={{ marginTop: 6 }}>{hint}</div>}
    </div>
  )
}

export function Loading() {
  return (
    <div className="empty">
      <div className="spinner" style={{ margin: '0 auto' }} />
    </div>
  )
}

/**
 * Запис власної вимови і порівняння зі зразком.
 *
 * Сенс вправи саме в порівнянні: почути себе одразу після еталона —
 * найшвидший спосіб помітити, що ł звучить як «л», а не як «в».
 * Останній запис зберігається, щоб можна було повернутись до нього пізніше.
 */
export function RecordCompare({ phraseId, audio, text }: { phraseId: string; audio?: string; text: string }) {
  const [state, setState] = useState<'idle' | 'recording' | 'ready'>('idle')
  const [blob, setBlob] = useState<Blob | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const recorder = useRef<Recorder | null>(null)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    let alive = true
    recordings.get(`say:${phraseId}`).then((b) => { if (alive && b) { setBlob(b); setState('ready') } }).catch(() => undefined)
    return () => {
      alive = false
      recorder.current?.cancel()
      if (timer.current) clearInterval(timer.current)
    }
  }, [phraseId])

  if (!canRecord()) return null

  const start = async () => {
    setError(null)
    try {
      recorder.current = await startRecording()
      setState('recording')
      setElapsed(0)
      timer.current = window.setInterval(() => setElapsed((s) => s + 1), 1000)
    } catch {
      setError('Немає доступу до мікрофона. Дозволь його в налаштуваннях браузера.')
    }
  }

  const stop = async () => {
    if (timer.current) clearInterval(timer.current)
    const rec = recorder.current
    recorder.current = null
    if (!rec) return
    const result = await rec.stop()
    setBlob(result)
    setState('ready')
    void recordings.put(`say:${phraseId}`, result).catch(() => undefined)
  }

  const compare = async () => {
    await play(audio, { text })
    await new Promise((r) => setTimeout(r, 350))
    if (blob) playBlob(blob)
  }

  return (
    <div className="stack--tight stack" style={{ width: '100%' }}>
      {state === 'idle' && (
        <button className="btn btn--wide" onClick={start}>🎤 Записати свою вимову</button>
      )}
      {state === 'recording' && (
        <button className="btn btn--wide btn--danger" onClick={stop}>
          ⏹ Зупинити · {elapsed} с
        </button>
      )}
      {state === 'ready' && blob && (
        <>
          <button className="btn btn--wide" onClick={compare}>▶︎ Зразок, потім я</button>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn" style={{ flex: 1 }} onClick={() => playBlob(blob)}>Тільки я</button>
            <button className="btn" style={{ flex: 1 }} onClick={start}>Записати ще</button>
          </div>
        </>
      )}
      {error && <div className="feedback feedback--no">{error}</div>}
    </div>
  )
}
